import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, type RouteObject } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import {
  AppCrashBoundary,
  announceQueryFailure,
  reloadOnceForStaleCode,
  retryQuery,
  retryQueryDelay,
  RootErrorPage,
} from "@/components/errors";
import { api, isSessionFailure } from "@/lib/api";
import { isQuietPreload } from "@/lib/lazy-route";
import { prefetchAfterRestore, prefetchBoot } from "@/lib/route-prefetch";
import { persistQueries, restorePersistedQueries } from "@/lib/persist-cache";
import { sessionKey } from "@/hooks/use-session";
import { App } from "@/app";
// Initialises i18next and sets the number and date locale; the first render
// waits for the active language's catalog, so the first paint is translated.
import { i18nReady } from "@/i18n";
import "./index.css";

/**
 * A tab left open across a deploy asks for chunks the new build no longer has
 * (their names are content hashes). Reload once to pick up the new build rather
 * than leave a blank page. The guard is shared with the error screens
 * (`components/errors/stale.ts`): within a minute of that reload the import is
 * left to fail, and the screen around it offers "A newer version of the app is
 * available" instead of reloading in a loop.
 */
window.addEventListener("vite:preloadError", (event) => {
  // A page loaded ahead of a click (a hover, the idle preload) is nobody's to reload for: the click asks again.
  if (isQuietPreload()) return;
  if (reloadOnceForStaleCode()) event.preventDefault();
});

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    /**
     * A refusal that means "you are not who you were": signed out on another
     * device, disabled, or an admin took the role away. Re-reading the session
     * moves the app to the login or waiting screen on its own, instead of
     * leaving a page of errors until the next reload.
     *
     * The session query itself is skipped: it answers "signed out" with a 200,
     * so a failure there is the network, and re-asking would only loop.
     */
    onError: (error, query) => {
      // A read started ahead of its page (`lib/route-prefetch.ts`) that no page is watching yet: the page asks
      // again when it opens and reports its own failure; a refusal here may only mean a link this role lacks.
      if (query.meta?.prefetch === true && query.getObserversCount() === 0) return;
      if (query.queryKey[0] !== "session" && isSessionFailure(error)) {
        void queryClient.invalidateQueries({ queryKey: ["session"], exact: true });
        return;
      }
      // A refresh that failed while the page shows earlier data: one toast, with Retry.
      announceQueryFailure(queryClient, error, query);
    },
  }),
  // The same for writes: an admin demoted mid-session learns it from the save
  // that is refused, not only from the next read.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (!isSessionFailure(error)) return;
      void queryClient.invalidateQueries({ queryKey: ["session"], exact: true });
    },
  }),
  defaultOptions: {
    queries: {
      // Never retry a refusal (auth, not found): the answer will not change. An API restart (no answer, 502,
      // 503, 504, the proxy's bare 500) is retried quietly for about twenty seconds; anything else twice.
      retry: retryQuery,
      retryDelay: retryQueryDelay,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Dev only, absent from a build: a loader that fails, so the root error screen
 * can be seen (the rest of the crash lab is the app's `/__dev/crash`).
 */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: "/__dev/crash/loader",
        loader: () => {
          throw new Error("This loader failed on purpose (the crash lab)");
        },
        element: null,
        errorElement: <RootErrorPage />,
      },
    ]
  : [];

/**
 * A data router, so a page can refuse to be left (`useBlocker`, the save
 * bar's navigation blocker). One splat route renders the app, whose own
 * `<Routes>` stay as they are: descendant routes work under a data router.
 *
 * Its error element replaces React Router's developer page for a failure in
 * the shell itself; a failing page is caught nearer, in the content area
 * (`ContentBoundary` in app.tsx), so the sidebar survives it.
 */
const router = createBrowserRouter([...devRoutes, { path: "*", element: <App />, errorElement: <RootErrorPage /> }]);

/**
 * Who is signed in, asked for now rather than when the app first renders: the request picks up the response
 * index.html preloaded with the page, and the first render no longer waits for the catalog and then the
 * session in turn. The same options as `useSession`, so the hook finds it in the cache.
 */
const session = queryClient.fetchQuery({ queryKey: sessionKey, queryFn: api.session, staleTime: 5 * 60 * 1000, retry: false });
// The page being opened: its code and first reads, beside the session rather than after it.
prefetchBoot(queryClient, session);

// The last visit's leaderboard, lists and concepts, kept in this browser (`lib/persist-cache.ts`).
persistQueries(queryClient, sessionKey);

// A catalog that failed to load still renders: the keys show, and a reload fixes it. The first render also
// waits for the session (preloaded with the page, so it is usually in already): the app draws nothing but a
// blank until it knows who is signed in anyway, and knowing it first is what lets the kept reads be restored,
// to the same person only, before any page reads the cache.
void Promise.all([i18nReady.catch(() => undefined), session.catch(() => null)])
  .then(([, signedIn]) => {
    restorePersistedQueries(queryClient, signedIn);
    prefetchAfterRestore(queryClient);
  })
  .catch(() => undefined)
  .then(() => createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppCrashBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider delayDuration={200}>
            <RouterProvider router={router} />
            <Toaster position="top-center" richColors />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AppCrashBoundary>
  </StrictMode>,
));
